/** This reducer handles all the api event booleans like save, loading, error, etc
 * The main use is to conditional render components in the ui when api requests are triggered
 */

export const API_EVENT_SAVING = 'API_EVENT_SAVING';
export const API_EVENT_SAVED = 'API_EVENT_SAVED';
export const API_EVENT_SUBMITTING = 'API_EVENT_SUBMITTING';
export const API_EVENT_SUBMITTED = 'API_EVENT_SUBMITTED';
export const API_EVENT_ERROR = 'API_EVENT_ERROR';
export const API_EVENT_LOADING = 'API_EVENT_LOADING';
export const API_EVENT_UPLOAD_LOADING = 'API_EVENT_UPLOAD_LOADING';
export const API_EVENT_RESET = 'API_EVENT_RESET';

export type ApiEventState = {
  isSaving: boolean;
  isSaved: boolean;
  isSubmitting: boolean;
  isSubmitted: boolean;
  isError: boolean;
  isLoading: boolean;
  uploadIsLoading: boolean;
};

export const apiEventInitial: ApiEventState = {
  isSaving: false,
  isSaved: false,
  isSubmitting: false,
  isSubmitted: false,
  isError: false,
  isLoading: false,
  uploadIsLoading: false
};

type ApiEventAction =
  | { type: typeof API_EVENT_SAVING }
  | { type: typeof API_EVENT_SAVED }
  | { type: typeof API_EVENT_SUBMITTING }
  | { type: typeof API_EVENT_SUBMITTED }
  | { type: typeof API_EVENT_ERROR }
  | { type: typeof API_EVENT_LOADING }
  | { type: typeof API_EVENT_UPLOAD_LOADING }
  | { type: typeof API_EVENT_RESET }
  | { type?: string };

export function apiEvent(
  state: ApiEventState | undefined,
  action: ApiEventAction
): ApiEventState {
  if (!state) {
    state = apiEventInitial;
  }

  switch (action.type) {
    case API_EVENT_RESET:
      return apiEventInitial;
    case API_EVENT_SAVING:
      return {
        ...state,
        isSaving: true,
        isSaved: false,
        isError: false,
        isSubmitting: false,
        isSubmitted: false,
        uploadIsLoading: false,
        isLoading: false
      };
    case API_EVENT_SAVED:
      return {
        ...state,
        isSaving: false,
        isSaved: true,
        isSubmitting: false,
        isSubmitted: false,
        isError: false,
        uploadIsLoading: false,
        isLoading: false
      };
    case API_EVENT_SUBMITTING:
      return {
        ...state,
        isSaving: false,
        isSaved: false,
        isError: false,
        isSubmitting: true,
        isSubmitted: false,
        uploadIsLoading: false,
        isLoading: false
      };
    case API_EVENT_SUBMITTED:
      return {
        ...state,
        isSaving: false,
        isSaved: false,
        isSubmitting: false,
        isSubmitted: true,
        isError: false,
        uploadIsLoading: false,
        isLoading: false
      };
    case API_EVENT_ERROR:
      return {
        ...state,
        isSaving: false,
        isSaved: false,
        isSubmitting: false,
        isSubmitted: false,
        isError: true,
        uploadIsLoading: false,
        isLoading: false
      };
    case API_EVENT_LOADING:
      return {
        ...state,
        isSaving: false,
        isSaved: false,
        isSubmitting: false,
        isSubmitted: false,
        isError: false,
        uploadIsLoading: false,
        isLoading: true
      };
    case API_EVENT_UPLOAD_LOADING:
      return {
        ...state,
        isSaving: false,
        isSaved: false,
        isSubmitting: false,
        isSubmitted: false,
        isError: false,
        uploadIsLoading: true,
        isLoading: false
      };
    default:
      return state;
  }
}

export function isSaving(): { type: typeof API_EVENT_SAVING } {
  return { type: API_EVENT_SAVING };
}

export function isSaved(): { type: typeof API_EVENT_SAVED } {
  return { type: API_EVENT_SAVED };
}

export function isSubmitting(): { type: typeof API_EVENT_SUBMITTING } {
  return { type: API_EVENT_SUBMITTING };
}

export function isSubmitted(): { type: typeof API_EVENT_SUBMITTED } {
  return { type: API_EVENT_SUBMITTED };
}

export function isError(): { type: typeof API_EVENT_ERROR } {
  return { type: API_EVENT_ERROR };
}

export function isLoading(): { type: typeof API_EVENT_LOADING } {
  return { type: API_EVENT_LOADING };
}

export function uploadIsLoading(): { type: typeof API_EVENT_UPLOAD_LOADING } {
  return { type: API_EVENT_UPLOAD_LOADING };
}

export function apiEventReset(): { type: typeof API_EVENT_RESET } {
  return { type: API_EVENT_RESET };
}